import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

function AuthModal({ onClose, onAuthSuccess }) {
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmCode, setConfirmCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const { login, signup, confirmSignup, resendCode } = useAuth();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');
    const result = await login(email, password);
    if (result.success) {
      if (onAuthSuccess) onAuthSuccess();
      onClose();
    } else {
      if (result.needsConfirmation) {
        setMode('confirm');
        setMessage('Please verify your email to continue');
      } else {
        setError(result.error);
      }
    }
    setLoading(false);
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      setLoading(false);
      return;
    }
    const result = await signup(email, password);
    if (result.success) {
      if (result.needsConfirmation) {
        setMode('confirm');
        setMessage('Check your email for a verification code');
      } else {
        await login(email, password);
        if (onAuthSuccess) onAuthSuccess();
        onClose();
      }
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const result = await confirmSignup(email, confirmCode);
    if (result.success) {
      const loginResult = await login(email, password);
      if (loginResult.success) {
        if (onAuthSuccess) onAuthSuccess();
        onClose();
      } else {
        setError('Email verified! Please log in.');
        setMode('login');
      }
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const handleResendCode = async () => {
    setLoading(true);
    setError('');
    const result = await resendCode(email);
    if (result.success) {
      setMessage('Verification code sent!');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-md bg-white rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-2">
          <h2 className="text-xl font-bold text-on-surface">
            {mode === 'login' && 'Welcome Back'}
            {mode === 'signup' && 'Create Account'}
            {mode === 'confirm' && 'Verify Email'}
          </h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-surface-container transition-colors">
            <span className="material-symbols-outlined text-on-surface-variant">close</span>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 pb-6 pt-2">
          {error && (
            <div className="mb-4 px-4 py-3 bg-error-container rounded-xl">
              <p className="text-sm text-error">{error}</p>
            </div>
          )}
          {message && (
            <div className="mb-4 px-4 py-3 bg-success-light rounded-xl">
              <p className="text-sm text-success">{message}</p>
            </div>
          )}

          {mode === 'login' && (
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label htmlFor="login-email" className="block text-sm font-medium text-on-surface mb-1">Email</label>
                <input
                  id="login-email" type="email" placeholder="you@example.com"
                  value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email"
                  className="w-full px-4 py-3 bg-surface-container-low border border-outline-variant/40 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                />
              </div>
              <div>
                <label htmlFor="login-password" className="block text-sm font-medium text-on-surface mb-1">Password</label>
                <input
                  id="login-password" type="password" placeholder="Your password"
                  value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password"
                  className="w-full px-4 py-3 bg-surface-container-low border border-outline-variant/40 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                />
              </div>
              <button type="submit" disabled={loading}
                className="w-full py-3.5 bg-gradient-to-r from-primary via-primary to-primary-dark text-on-primary rounded-2xl text-sm font-semibold hover:shadow-md active:scale-[0.97] duration-200 transition-all disabled:opacity-50"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
              <p className="text-center text-sm text-on-surface-variant">
                Don't have an account?{' '}
                <button type="button" onClick={() => { setMode('signup'); setError(''); setMessage(''); }}
                  className="text-primary font-semibold hover:underline"
                >
                  Sign up
                </button>
              </p>
            </form>
          )}

          {mode === 'signup' && (
            <form onSubmit={handleSignup} className="space-y-4">
              <div>
                <label htmlFor="signup-email" className="block text-sm font-medium text-on-surface mb-1">Email</label>
                <input
                  id="signup-email" type="email" placeholder="you@example.com"
                  value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email"
                  className="w-full px-4 py-3 bg-surface-container-low border border-outline-variant/40 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                />
              </div>
              <div>
                <label htmlFor="signup-password" className="block text-sm font-medium text-on-surface mb-1">Password</label>
                <input
                  id="signup-password" type="password" placeholder="At least 8 characters"
                  value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} autoComplete="new-password"
                  className="w-full px-4 py-3 bg-surface-container-low border border-outline-variant/40 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                />
                <p className="text-xs text-on-surface-variant mt-1">Must include uppercase, lowercase, and numbers</p>
              </div>
              <button type="submit" disabled={loading}
                className="w-full py-3.5 bg-gradient-to-r from-primary via-primary to-primary-dark text-on-primary rounded-2xl text-sm font-semibold hover:shadow-md active:scale-[0.97] duration-200 transition-all disabled:opacity-50"
              >
                {loading ? 'Creating account...' : 'Create Account'}
              </button>
              <p className="text-center text-sm text-on-surface-variant">
                Already have an account?{' '}
                <button type="button" onClick={() => { setMode('login'); setError(''); setMessage(''); }}
                  className="text-primary font-semibold hover:underline"
                >
                  Sign in
                </button>
              </p>
            </form>
          )}

          {mode === 'confirm' && (
            <form onSubmit={handleConfirm} className="space-y-4">
              <p className="text-sm text-on-surface-variant">
                We sent a verification code to <strong className="text-on-surface">{email}</strong>
              </p>
              <div>
                <label htmlFor="confirm-code" className="block text-sm font-medium text-on-surface mb-1">Verification Code</label>
                <input
                  id="confirm-code" type="text" placeholder="Enter 6-digit code"
                  value={confirmCode} onChange={(e) => setConfirmCode(e.target.value)} required
                  autoComplete="one-time-code" inputMode="numeric" pattern="[0-9]*"
                  className="w-full px-4 py-3 bg-surface-container-low border border-outline-variant/40 rounded-2xl text-sm text-center tracking-[0.3em] font-semibold focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                />
              </div>
              <button type="submit" disabled={loading}
                className="w-full py-3.5 bg-gradient-to-r from-primary via-primary to-primary-dark text-on-primary rounded-2xl text-sm font-semibold hover:shadow-md active:scale-[0.97] duration-200 transition-all disabled:opacity-50"
              >
                {loading ? 'Verifying...' : 'Verify Email'}
              </button>
              <p className="text-center text-sm text-on-surface-variant">
                Didn't receive code?{' '}
                <button type="button" onClick={handleResendCode} disabled={loading}
                  className="text-primary font-semibold hover:underline disabled:opacity-50"
                >
                  Resend
                </button>
              </p>
            </form>
          )}

          <div className="mt-6 pt-4 border-t border-outline-variant/15 text-center">
            <p className="text-xs text-on-surface-variant mb-2">Or continue without an account</p>
            <button type="button" onClick={onClose}
              className="text-sm font-medium text-on-surface-variant hover:text-on-surface transition-colors"
            >
              Continue as Guest
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AuthModal;
